import { collectFilesRecursivelyFromItems } from "@components/common/sphere-dropzone/collect-files";
import { DropHandler } from "@components/common/sphere-dropzone/drop-handler";
import { DropzoneElements } from "@components/common/sphere-dropzone/dropzone-elements";
import { assert } from "@faro-lotv/foundation";
import { FontWeights, Text, useToast } from "@flat-ui";
import { isMaliciousSvgFile, validateFile } from "@hooks/file-upload-utils";
import { haveFileNamesValidLength, MAX_FILE_NAME_LENGTH } from "@hooks/upload-tasks/upload-tasks-utils";
import { useFileUpload } from "@hooks/use-file-upload";
import { Box } from "@mui-wrapper";
import { FILE_SIZE_MULTIPLIER } from "@utils/file-utils";
import { compressImageFile, MAX_THUMBNAIL_UPLOAD_WIDTH } from "@utils/image-utils";
import { sentryCaptureError } from "@utils/sentry-utils";
import { isNumber } from "es-toolkit";
import { useCallback, useState } from "react";
/** Contains all the sorted default file extensions that the default dropzone support  */
const DEFAULT_FILE_EXTENSIONS_WHITE_LIST = ["gif", "jpeg", "jpg", "png", "svg"];

/** Default maximum MB size of the file to upload */
const DEFAULT_MAX_UPLOAD_SIZE_MB = 10;

/** Maximum MB size of the file to use normal upload, higher than that will use chunk upload */
const MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_MB = 5;

/** Maximum byte size of the file to use normal upload, higher than that will use chunk upload */
const MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_BYTES = MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_MB * FILE_SIZE_MULTIPLIER * FILE_SIZE_MULTIPLIER;

/**
 * Renders the dropzone area to upload a file
 */
export function SphereDropzone({
  avatar,
  existingImageUrl,
  isLoading,
  setIsLoading,
  onUploadComplete,
  onDeleteButtonClick,
  deleteIconTooltipText,
  titleDragAndDrop,
  hasUploadFileButton,
  shouldShowSupportedFormats,
  shouldShowSizeLimit,
  allowedExtensions = DEFAULT_FILE_EXTENSIONS_WHITE_LIST,
  instruction,
  maxFileSize = DEFAULT_MAX_UPLOAD_SIZE_MB,
  shouldAllowMultiUpload = false,
  shouldAllowFolderUpload = false,
  shouldAllowFiles = true,
  context,
  onSelectFiles,
  shouldCompressImage = false
}) {
  const {
    uploadSingleFile,
    uploadFileWithChunks,
    uploadMultipleFiles,
    validateAndAddFailedTask
  } = useFileUpload();
  const {
    openToast
  } = useToast();
  const [fileInputEl, setFileInputEl] = useState(null);
  const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const openFileExplorer = useCallback(() => {
    if (!fileInputEl) {
      return;
    }
    // Allow to select folders only when openFileExplorer
    if (shouldAllowFiles === false) {
      // needed to to this cause react does not allow to set these attributes in the DOM/HTML
      // ... is not assignable to type 'DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
      fileInputEl.setAttribute("webkitdirectory", "true");
      fileInputEl.setAttribute("directory", "true");
    }
    // https://stackoverflow.com/a/76926836/4555850
    // We need oncancel to make this work: canBeDropped={!isFileExplorerOpen}.
    // eslint-disable-next-line react-compiler/react-compiler -- DOM element mutation in callback, not during render
    fileInputEl.oncancel = () => setIsFileExplorerOpen(false);
    fileInputEl.click();
    setIsFileExplorerOpen(true);
  }, [fileInputEl, shouldAllowFiles]);

  /** Selects files from file explorer after click on the dropzone */
  function selectFileFromDialog(event) {
    setIsFileExplorerOpen(false);
    // When a folder was selected, we get the individual files instead of a FileSystemDirectoryHandle/Entry.
    // But we could still recover the folder structure using files[*].webkitRelativePath.
    const files = event.target.files;
    if (files) {
      const areValidFilesNames = haveFileNamesValidLength(files);
      if (!areValidFilesNames) {
        openToast({
          title: `Folder or file name is too large, please change it to be shorter than ${MAX_FILE_NAME_LENGTH + 1} characters`,
          variant: "warning"
        });
        return;
      }
    }
    handleFileSelection(files);
  }

  /** Selects files from drag and drop */
  async function selectFileFromDrop(event_0) {
    event_0.preventDefault();
    if (event_0.dataTransfer) {
      /* Validate files/folders names, if too long it can lead to error or unexpected behavior */
      const areValidFilesNames_0 = haveFileNamesValidLength(event_0.dataTransfer.files);
      if (!areValidFilesNames_0) {
        openToast({
          title: `Folder or file name is too large, please change it to be shorter than ${MAX_FILE_NAME_LENGTH + 1} characters`,
          variant: "warning"
        });
        return;
      }
      if (event_0.dataTransfer.items?.length >= 0) {
        const files_0 = await collectFilesRecursivelyFromItems(event_0.dataTransfer.items, shouldAllowFolderUpload, shouldAllowFiles, allowedExtensions);
        handleFileSelection(files_0);
      } else {
        handleFileSelection(event_0.dataTransfer.files);
      }
    }
  }

  /** Handle files after selection or drop */
  function handleFileSelection(files_1) {
    // Early return if there is no file dropped
    if (!files_1 || files_1.length === 0) {
      return;
    }
    const allowedFiles = [];
    for (const file of files_1) {
      const validatedFile = validateFile({
        file,
        allowedExtensions,
        maxFileSize
      });
      if (validatedFile.isValid) {
        allowedFiles.push(file);
      }
    }

    // Waiting for confirmation to start uploading if onSelectFiles callback is provided.
    if (onSelectFiles) {
      onSelectFiles(allowedFiles, () => {
        if (shouldAllowMultiUpload) {
          void initiateMultipleFileUpload(allowedFiles);
        } else {
          void initiateFileUpload(allowedFiles[0]);
        }
      });
      return;
    }

    // Proceed uploading if there is no confirmation required
    shouldAllowMultiUpload ? void initiateMultipleFileUpload(files_1) : void initiateFileUpload(files_1[0]);
  }

  /** Triggers when file upload starts */
  function onUploadStart() {
    // Progress loader should not visible to make the dropzone available for further upload
    if (!shouldAllowMultiUpload) {
      setIsLoading(true);
    }
  }

  /** Triggers to set the file upload progress value */
  function onUploadProgress(progress) {
    const uploadProgress_0 = isNumber(progress) ? progress : progress.loaded / progress.total * 100;
    setUploadProgress(uploadProgress_0);
  }

  /** Triggers when file is selected or dropped */
  async function initiateFileUpload(file_0) {
    // Early return if file is not available
    if (!file_0) {
      return;
    }
    const hasMaliciousCode = await isMaliciousSvgFile(file_0);
    if (hasMaliciousCode) {
      openToast({
        title: "Invalid SVG file",
        message: "This SVG contains potentially unsafe content.",
        variant: "error"
      });
      return;
    }
    const validatedFile_0 = validateFile({
      file: file_0,
      allowedExtensions,
      maxFileSize
    });
    if (!validatedFile_0.isValid) {
      openToast({
        title: validatedFile_0.message ?? "Invalid file",
        message: validatedFile_0.description,
        variant: "error"
      });
      return;
    }

    // Need to reset the progress to start from 0 next time
    setUploadProgress(0);
    assert(context, "SphereDropzone: Upload context is undefined, but required in this case");

    // Compress image before upload if shouldCompressImage is true and file is an image
    let fileToUpload = file_0;
    if (shouldCompressImage && file_0.type.startsWith("image/")) {
      try {
        fileToUpload = await compressImageFile(file_0, MAX_THUMBNAIL_UPLOAD_WIDTH);
      } catch (error) {
        // If compression fails, fallback to uploading the original file
        sentryCaptureError({
          error,
          title: "Failed to compress image"
        });
        fileToUpload = file_0;
      }
    }
    const uploadParams = {
      file: fileToUpload,
      onUploadStart,
      onUploadProgress,
      onUploadComplete,
      context
    };

    // CoreAPI complains if we request a chunked upload without having a project ID.
    const isLargeFile = file_0.size > MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_BYTES;
    const shouldUseChunkUpload = !!context.projectId && isLargeFile;
    if (!shouldUseChunkUpload && isLargeFile) {
      // eslint-disable-next-line no-console -- Should be visible in development.
      console.warn("Forcing non-chunked upload for large file because project ID was not provided.");
    }

    // Use chunk upload if file size is bigger than MAX_FILE_SIZE_FOR_NORMAL_UPLOAD_MB.
    const isUploaded = shouldUseChunkUpload ? await uploadFileWithChunks(uploadParams) : await uploadSingleFile(uploadParams);

    // Hiding the loader spinner if upload is failed
    if (!isUploaded) {
      setIsLoading(false);
    }
  }
  async function initiateMultipleFileUpload(files_2) {
    // Early return if projectId is not provided
    // Only for type checking
    assert(context, "SphereDropzone: Upload context is undefined, but required in this case");
    if (!context.projectId) {
      return;
    }
    const uploadableFiles = [...files_2].filter(file_1 => validateAndAddFailedTask({
      file: file_1,
      allowedExtensions,
      maxFileSize,
      context
    }));

    // Return if there is no uploadable file
    if (!uploadableFiles.length) {
      return;
    }
    const uploadParams_0 = {
      files: uploadableFiles,
      onUploadStart,
      onUploadProgress,
      onUploadComplete,
      context
    };
    await uploadMultipleFiles(uploadParams_0);
  }
  return <Box component="div" data-testid="dropzone-container">
      {(titleDragAndDrop || hasUploadFileButton) && <Box sx={{
      display: "flex",
      justifyContent: "space-between"
    }}>
          {titleDragAndDrop && <Text variant="heading14" sx={{
        color: "text.primary",
        mb: "10px"
      }}>
              {titleDragAndDrop}
            </Text>}

          {hasUploadFileButton && <Text variant="bodyS" sx={{
        fontWeight: FontWeights.SemiBold,
        color: "primary.main",
        mb: "10px",
        textDecoration: "underline",
        cursor: "pointer"
      }} onClick={openFileExplorer}>
              Upload file
            </Text>}
        </Box>}
      <DropHandler onDrop={selectFileFromDrop} canBeDropped={!isFileExplorerOpen} onClick={openFileExplorer} isLoading={isLoading} shouldAllowMultiUpload={shouldAllowMultiUpload}>
        <DropzoneElements avatar={avatar} isLoading={isLoading} uploadProgress={uploadProgress} existingImageUrl={existingImageUrl} setFileInputEl={setFileInputEl} selectFileFromDialog={selectFileFromDialog} onDeleteButtonClick={onDeleteButtonClick} deleteIconTooltipText={deleteIconTooltipText} shouldShowSupportedFormats={shouldShowSupportedFormats} shouldShowSizeLimit={shouldShowSizeLimit} allowedExtensions={allowedExtensions} instruction={instruction} maxFileSize={maxFileSize} shouldAllowMultiUpload={shouldAllowMultiUpload} shouldAllowFiles={shouldAllowFiles} />
      </DropHandler>
    </Box>;
}